import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  TrendingUp as CommissionIcon,
  AccountBalance as RevenueIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { CommissionStats } from '../../types';

const CommissionTrackingPage: React.FC = () => {
  const [stats, setStats] = useState<CommissionStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchCommissionStats = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      // Calculate stats from payments
      const paymentsResponse = await apiService.getPayments({ per_page: 1000 });
      const payments = paymentsResponse.data;

      const totalRevenue = payments.reduce((sum, p) => sum + parseFloat(p.amount.toString()), 0);
      const totalCommission = payments.reduce((sum, p) => sum + parseFloat(p.commission_amount.toString()), 0);
      const totalPlatformFee = payments.reduce((sum, p) => sum + parseFloat(p.platform_fee.toString()), 0);
      const commissionPercentage = totalRevenue > 0 ? (totalCommission / totalRevenue) * 100 : 0;

      // Group by month
      const commissionByMonth: { [key: string]: number } = {};
      payments.forEach(payment => {
        const month = new Date(payment.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short' });
        if (!commissionByMonth[month]) {
          commissionByMonth[month] = 0;
        }
        commissionByMonth[month] += parseFloat(payment.commission_amount.toString());
      });

      // Top professionals
      const professionalEarnings: { [key: number]: { name: string; earned: number; commission: number } } = {};
      payments.forEach(payment => {
        if (payment.professional_id) {
          if (!professionalEarnings[payment.professional_id]) {
            professionalEarnings[payment.professional_id] = {
              name: payment.professional?.name || 'Unknown',
              earned: 0,
              commission: 0,
            };
          }
          professionalEarnings[payment.professional_id].earned += parseFloat(payment.net_amount.toString());
          professionalEarnings[payment.professional_id].commission += parseFloat(payment.commission_amount.toString());
        }
      });

      const topProfessionals = Object.entries(professionalEarnings)
        .map(([id, data]) => ({
          professional_id: parseInt(id),
          professional_name: data.name,
          total_earned: data.earned,
          commission: data.commission,
        }))
        .sort((a, b) => b.commission - a.commission)
        .slice(0, 10);

      const calculatedStats: CommissionStats = {
        total_commission: totalCommission,
        commission_percentage: commissionPercentage,
        total_platform_fee: totalPlatformFee,
        total_revenue: totalRevenue,
        commission_by_month: Object.entries(commissionByMonth).map(([month, commission]) => ({
          month,
          commission,
        })),
        top_professionals: topProfessionals,
      };

      setStats(calculatedStats);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load commission statistics');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCommissionStats();
  }, [fetchCommissionStats]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Commission Tracking
      </Typography>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <CommissionIcon sx={{ fontSize: 48, color: '#1976D2' }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Commission
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                    ₹{stats.total_commission.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <RevenueIcon sx={{ fontSize: 48, color: '#2E7D32' }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                    ₹{stats.total_revenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <PercentIcon sx={{ fontSize: 48, color: '#7B1FA2' }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Commission Rate
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#7B1FA2' }}>
                    {stats.commission_percentage.toFixed(2)}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Commission by Month */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Commission by Month
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Commission</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.commission_by_month.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.month}</TableCell>
                        <TableCell align="right">
                          ₹{item.commission.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Professionals */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Professionals by Commission
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Professional</TableCell>
                      <TableCell align="right">Total Earned</TableCell>
                      <TableCell align="right">Commission</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.top_professionals.map((prof) => (
                      <TableRow key={prof.professional_id}>
                        <TableCell>{prof.professional_name}</TableCell>
                        <TableCell align="right">
                          ₹{prof.total_earned.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align="right">
                          ₹{prof.commission.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3, backgroundColor: '#F5F5F5' }}>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  Platform Fee Collected
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  ₹{stats.total_platform_fee.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  Net Revenue (After Commission)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  ₹{(stats.total_revenue - stats.total_commission).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  Average Commission per Transaction
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  ₹{(stats.total_commission / Math.max(stats.commission_by_month.length, 1)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CommissionTrackingPage;

